import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  Box,
  Alert,
  useTheme,
} from '@mui/material'
import { formatCurrency } from '../../shared/formats'
import {
  PaymentRow,
  PaymentRowType,
  Scenario,
  ScheduleType,
} from '../../shared/types'
import SFDialog from '../../../lib/southfork/components/sf-dialog/sf-dialog'
import { Theme } from '@mui/material/styles'
import { EditableBidRow, EditableBidRowComponent } from './editable-bid-row'
import { BidTableRow } from './bids-table'
import {
  getRowPayoutValue,
  normalizePaymentRowToPercentage,
  paymentDateComparator,
} from '../create-proposal/conversion-utils'
import { useFeatureFlags } from '../../shared/use-feature-flags'
import { FeatureFlagName } from '../../../api/feature-flags-api'

interface FinalizeSelectionDialogProps {
  isOpen: boolean
  onClose: () => void
  onFinalize: (finalizedBids: FinalizedBid[]) => void
  selectedBids: BidTableRow[]
  scenarios: Scenario[]
  proposalDepositAmount: number
  isLoading?: boolean
  scheduleType?: ScheduleType
}

interface FinalizedBid {
  scenarioId: string
  issuerTenantId: string
  rate: number
  treasuryRate: number
  amount: number
  wamYears: number | null
  partialMaturities: {
    paymentDate: Date | string | null
    amount: number
    inputType: string
  }[]
}

export const FinalizeSelectionDialog: React.FC<
  FinalizeSelectionDialogProps
> = ({
  isOpen,
  onClose,
  onFinalize,
  selectedBids,
  scenarios,
  proposalDepositAmount,
  isLoading = false,
  scheduleType = ScheduleType.DEPOSIT,
}) => {
  const theme: Theme = useTheme()
  const { flags } = useFeatureFlags()
  const enableDollarAmount =
    flags[FeatureFlagName.ENABLE_PARTIAL_MATURITIES_BY_DOLLAR_AMOUNT]

  // Helper to parse formatted number strings
  const parseFormattedNumber = useCallback((value: string): number => {
    return parseFloat(value.replace(/,/g, ''))
  }, [])

  // Helper function to generate payment schedule from scenario
  const generatePaymentSchedule = useCallback(
    (scenario: Scenario) => {
      if (!scenario.paymentSchedule || scenario.paymentSchedule.length === 0) {
        return 'N/A'
      }

      return scenario.paymentSchedule
        .slice()
        .sort(paymentDateComparator)
        .map((payment) => {
          const date = payment.paymentDate
            ? new Date(payment.paymentDate).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: '2-digit',
                timeZone: 'UTC',
              })
            : 'N/A'
          const value = getRowPayoutValue(payment, enableDollarAmount)
          const isDollarRow =
            (payment.inputType ?? PaymentRowType.PERCENTAGE) ===
            PaymentRowType.DOLLAR
          const display =
            value > 0
              ? isDollarRow
                ? formatCurrency(value)
                : `${value}%`
              : 'N/A'
          return `${date} (${display})`
        })
        .join(', ')
    },
    [enableDollarAmount]
  )

  // Convert selected bids to editable format with additional required fields
  const initialEditableBids = useMemo(() => {
    // Windsurf generated
    // Evenly divide the deposit amount among all selected scenarios
    const numberOfScenarios = selectedBids.length
    const baseAmount = Math.floor(proposalDepositAmount / numberOfScenarios)
    const remainder = proposalDepositAmount - baseAmount * numberOfScenarios

    return selectedBids.map((bid, index): EditableBidRow => {
      // Find the corresponding scenario using direct ID lookup (reliable!)
      const scenario = scenarios.find((s) => s.id === bid.scenarioId)

      // Distribute remainder by adding 1 to the last scenarios
      const amount =
        baseAmount + (index >= numberOfScenarios - remainder ? 1 : 0)

      const rawSchedule = scenario?.paymentSchedule || []
      const clonedSchedule = rawSchedule
        .map((row) => ({ ...row }))
        .sort(paymentDateComparator)

      return {
        ...bid,
        negotiatedRate: bid.rate, // Initialize with current rate
        treasuryRate: bid.treasuryRate, // Initialize with original treasury rate
        amount: amount.toLocaleString('en-US'), // Initialize with evenly divided amount
        scenarioId: bid.scenarioId, // Use the scenarioId directly from row data
        issuerTenantId: bid.issuerTenantId, // Use the issuerTenantId directly from row data
        wamYears: scenario?.wamYears || null,
        paymentSchedule: scenario ? generatePaymentSchedule(scenario) : 'N/A',
        editablePaymentSchedule: clonedSchedule,
        // Sorted ascending by date — intentionally not the backend's raw order,
        // so that "Reset" restores a consistently sorted schedule.
        originalPaymentSchedule: rawSchedule
          .map((row) => ({ ...row }))
          .sort(paymentDateComparator),
      }
    })
  }, [selectedBids, scenarios, generatePaymentSchedule, proposalDepositAmount])

  const [editableBids, setEditableBids] =
    useState<EditableBidRow[]>(initialEditableBids)

  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set())
  const [expandedPaymentSchedules, setExpandedPaymentSchedules] = useState<
    Set<string>
  >(new Set())

  // Reset only when the actual set of selected bids changes (not when the
  // parent re-renders with a new array reference). Without this guard, every
  // parent render would clobber the user's typed values back to the bid's
  // original rate/treasury/amount.
  const selectedBidsKey = useMemo(
    () => selectedBids.map((b) => b.bidId).join('|'),
    [selectedBids]
  )
  const initialBidsRef = useRef(initialEditableBids)
  initialBidsRef.current = initialEditableBids
  useEffect(() => {
    setEditableBids(initialBidsRef.current)
  }, [selectedBidsKey])

  const toggleNotes = useCallback(
    (bidId: string) => {
      setExpandedNotes((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(bidId)) {
          newSet.delete(bidId)
        } else {
          newSet.add(bidId)
        }
        return newSet
      })
    },
    [setExpandedNotes]
  )

  const togglePaymentSchedule = useCallback(
    (bidId: string) => {
      setExpandedPaymentSchedules((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(bidId)) {
          newSet.delete(bidId)
        } else {
          newSet.add(bidId)
        }
        return newSet
      })
    },
    [setExpandedPaymentSchedules]
  )

  // Validation: check if all required fields are filled
  const isValid = useMemo(() => {
    return editableBids.every((bid) => {
      const amount = bid.amount ? parseFormattedNumber(bid.amount) : 0

      const allRowsValid = bid.editablePaymentSchedule.every((row) => {
        const value = getRowPayoutValue(row, enableDollarAmount)
        const isPercentageRow =
          (row.inputType ?? PaymentRowType.PERCENTAGE) ===
          PaymentRowType.PERCENTAGE
        return (
          value > 0 &&
          (!isPercentageRow || value <= 100) &&
          row.paymentDate !== null
        )
      })

      const totalPercentage =
        Math.round(
          bid.editablePaymentSchedule.reduce(
            (sum, row) =>
              sum +
              normalizePaymentRowToPercentage(
                row,
                enableDollarAmount,
                proposalDepositAmount
              ),
            0
          ) * 1000
        ) / 1000

      const percentageValid =
        scheduleType !== ScheduleType.DEPOSIT ||
        Math.abs(totalPercentage - 100) < 0.001

      return (
        bid.negotiatedRate !== null &&
        bid.negotiatedRate > 0 &&
        bid.treasuryRate !== null &&
        bid.treasuryRate >= 0 &&
        amount > 0 &&
        allRowsValid &&
        percentageValid
      )
    })
  }, [
    editableBids,
    parseFormattedNumber,
    scheduleType,
    enableDollarAmount,
    proposalDepositAmount,
  ])

  // Calculate total amount entered by user
  const totalAmount = useMemo(() => {
    return editableBids.reduce((sum, bid) => {
      const amount = bid.amount ? parseFormattedNumber(bid.amount) : 0
      return sum + amount
    }, 0)
  }, [editableBids, parseFormattedNumber])

  // Check if amounts don't match proposal deposit (only when all amounts are entered)
  const hasAmountMismatch = useMemo(() => {
    const allAmountsEntered = editableBids.every((bid) => {
      const amount = bid.amount ? parseFormattedNumber(bid.amount) : 0
      return amount > 0
    })
    return allAmountsEntered && totalAmount !== proposalDepositAmount
  }, [editableBids, totalAmount, proposalDepositAmount, parseFormattedNumber])

  const updateBidField = useCallback(
    (
      bidId: string,
      field: keyof EditableBidRow,
      value: number | string | null | PaymentRow[]
    ) => {
      setEditableBids((prev) =>
        prev.map((bid) =>
          bid.bidId === bidId ? { ...bid, [field]: value } : bid
        )
      )
    },
    []
  )

  const handleFinalize = useCallback(() => {
    if (!isValid) return

    const finalizedBids: FinalizedBid[] = editableBids.map((bid) => ({
      scenarioId: bid.scenarioId,
      issuerTenantId: bid.issuerTenantId,
      rate: bid.negotiatedRate!,
      treasuryRate: bid.treasuryRate || 0,
      amount: parseFormattedNumber(bid.amount),
      wamYears: bid.wamYears,
      partialMaturities: bid.editablePaymentSchedule.map((row) => ({
        paymentDate: row.paymentDate
          ? new Date(row.paymentDate).toISOString().split('T')[0]
          : null,
        amount: getRowPayoutValue(row, enableDollarAmount),
        inputType: row.inputType ?? PaymentRowType.PERCENTAGE,
      })),
    }))

    onFinalize(finalizedBids)
  }, [
    editableBids,
    isValid,
    onFinalize,
    parseFormattedNumber,
    enableDollarAmount,
  ])

  const bidRows = useMemo(
    () =>
      editableBids.map((bid) => (
        <EditableBidRowComponent
          key={`finalize--editable-bid--${bid.bidId}`}
          bid={bid}
          expandedNotes={expandedNotes}
          expandedPaymentSchedules={expandedPaymentSchedules}
          toggleNotes={toggleNotes}
          togglePaymentSchedule={togglePaymentSchedule}
          updateBidField={updateBidField}
          parseFormattedNumber={parseFormattedNumber}
          theme={theme}
          scheduleType={scheduleType}
          depositAmount={proposalDepositAmount}
        />
      )),
    [
      editableBids,
      expandedNotes,
      expandedPaymentSchedules,
      toggleNotes,
      togglePaymentSchedule,
      updateBidField,
      parseFormattedNumber,
      theme,
      scheduleType,
      proposalDepositAmount,
    ]
  )

  return (
    <SFDialog
      title="Finalize Proposal"
      isOpen={isOpen}
      onCancel={onClose}
      onOk={handleFinalize}
      okButtonText={isLoading ? 'Finalizing...' : 'Finalize & Send Notices'}
      cancelButtonText="Back to Review"
      maxWidth="lg"
      isOkDisabled={!isValid || isLoading}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Review & Finalize Selection
        </Typography>

        {!isValid && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Please fill in all required fields (Negotiated Rate, Amount, and
            Payment Schedule) before finalizing.
          </Alert>
        )}

        {hasAmountMismatch && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Amount Total Mismatch:</strong> The total amount entered (
            {formatCurrency(totalAmount)}) does not match the proposal deposit
            amount ({formatCurrency(proposalDepositAmount)}). You can still
            proceed, but please verify the amounts are correct.
          </Alert>
        )}

        <Box sx={{ border: `1px solid ${theme.palette.grey[200]}` }}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '12%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '18%' }} />
            </colgroup>
            <TableHead
              sx={{
                '& .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.grey['100'],
                },
              }}
            >
              <TableRow>
                <TableCell>Issuer</TableCell>
                <TableCell align="right">Issuer Rate</TableCell>
                <TableCell>Treasury Rate</TableCell>
                <TableCell>Negotiated Rate</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell align="right">WAM (years)</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Payment Schedule</TableCell>
              </TableRow>
            </TableHead>
          </Table>
          <TableContainer sx={{ height: '500px' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '18%' }} />
              </colgroup>
              <TableBody>{bidRows}</TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>When you confirm:</strong>
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 0 }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Winning issuers will receive award notifications with the
              negotiated rates shown above.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              All other participating issuers will be notified that their bids
              were not selected.
            </Typography>
            <Typography component="li" variant="body2">
              No further changes can be made to this proposal.
            </Typography>
          </Box>
        </Box>
      </Box>
    </SFDialog>
  )
}
